import {
  Box,
  Divider,
  Grid,
  ListItem,
  ListItemText,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { FindingType, MqueryDocs, useGetCheckRowDataQuery } from "~/operations";
import { CheckFindingsCheckNode } from "~/pages/inventory/asset/types";
import { Markdown } from "../markdown";
import { ErrorBoundary } from "../error-boundary";
import { useFormattedAssetQuery } from "~/pages/findings/FindingOnAsset/hooks/useFormattedAssetQuery";
import { Code } from "../code";
import { Loading } from "../loading";
import { CheckResultIcon } from "./CheckResultIcon";

type FailedCheckProps = { assetMrn: string; check: CheckFindingsCheckNode };

export function FailedCheck({ assetMrn, check }: FailedCheckProps) {
  const { data, loading } = useGetCheckRowDataQuery({
    variables: {
      scopeMrn: assetMrn,
      first: 1,
      filter: {
        types: [FindingType.Check],
        mrn: check.mrn,
      },
      renderedAssetQueryInput: {
        assetMrn: assetMrn,
        queryMrn: check?.mrn || "",
      },
    },
  });

  const findingsUnion =
    data?.findings?.__typename === "FindingsConnection"
      ? data.findings
      : undefined;
  const checkEdges = findingsUnion?.edges || [];
  const checks = checkEdges.flatMap((e) =>
    e?.node?.__typename === "CheckFinding" ? e.node : [],
  );

  const checkData = checks?.[0];
  const mquery = checkData?.mquery;

  const notFoundErrorMessage =
    data?.renderedAssetQueryData?.__typename === "NotFoundError"
      ? data.renderedAssetQueryData.message
      : null;
  const renderedData =
    data?.renderedAssetQueryData?.__typename === "RenderedAssetQueryData"
      ? data.renderedAssetQueryData
      : null;

  const { results } = useFormattedAssetQuery(renderedData, check?.state);

  if (loading) {
    return (
      <Grid
        sx={{
          py: 4,
          borderBottom: "1px solid #DBDADD",
        }}
        size={12}
      >
        <Loading what="failed check" />
      </Grid>
    );
  }

  // extract remediation text from nested docs structure
  const getRemediationText = (remediations: MqueryDocs["remediations"]) => {
    if (
      remediations &&
      "entries" in remediations &&
      remediations.entries.length > 0
    ) {
      return remediations.entries.map((remediation) => (
        <Markdown key={remediation.id} source={remediation.desc} />
      ));
    }
    return null;
  };

  const remediationText = getRemediationText(mquery?.docs?.remediations);

  return (
    <Grid
      className="failed-check"
      sx={{
        py: 4,
      }}
      size={12}
    >
      <Typography
        variant="h5"
        component="h4"
        fontWeight={700}
        sx={{ mb: 2, px: 4 }}
      >
        {check?.title}
      </Typography>
      <Grid container columnGap={6} sx={{ px: 4 }}>
        <Grid size="grow">
          <Typography>{mquery?.docs?.desc || check?.title}</Typography>
        </Grid>
        <Grid size="auto">
          <CheckResultIcon
            scoreResult={check?.resultType}
            scoreState={check?.state}
          />
        </Grid>
      </Grid>
      <Grid container sx={{ px: 4 }}>
        {(results || notFoundErrorMessage) && (
          <ListItem disableGutters>
            <ListItemText
              primary={
                <Grid container>
                  <Grid container alignItems="center" mt={5} mb={3} size={12}>
                    <Grid sx={{ display: "flex", alignItems: "center" }}>
                      <Box>
                        <Typography
                          sx={{
                            display: "inline-block",
                            pr: 3,
                            fontWeight: 700,
                          }}
                        >
                          Result
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size="grow">
                      <Divider />
                    </Grid>
                  </Grid>
                  <Grid size={12}>
                    {notFoundErrorMessage && (
                      <Code className="ini">
                        {"[Query Error]\n" + notFoundErrorMessage}
                      </Code>
                    )}
                    {results}
                  </Grid>
                </Grid>
              }
            />
          </ListItem>
        )}
        {remediationText && (
          <ErrorBoundary
            key={`${check?.mrn}-remediation`}
            FallbackComponent={RowErrorFallback}
          >
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Grid container>
                    <Grid container alignItems="center" mt={5} mb={3} size={12}>
                      <Grid sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Typography
                            sx={{
                              display: "inline-block",
                              pr: 3,
                              fontWeight: 700,
                            }}
                          >
                            Remediation
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size="grow">
                        <Divider />
                      </Grid>
                    </Grid>
                    <Grid size={12}>{remediationText}</Grid>
                  </Grid>
                }
              />
            </ListItem>
          </ErrorBoundary>
        )}
      </Grid>
    </Grid>
  );
}

const RowErrorFallback = ({ error }: { error: Error }) => {
  console.error(error.message);
  return (
    <TableRow>
      <TableCell colSpan={5} sx={{ border: "none" }}></TableCell>
    </TableRow>
  );
};
